import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { 
  Grid, 
  Paper, 
  Button, 
  Typography, 
  TextField,
  Box,
  Collapse
} from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import { Autocomplete } from "@material-ui/lab";
import { toast } from "react-toastify";
import { Today, Edit, Close, Warning } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import CliniqueService from '../../services/CliniqueService';
import FournisseurService from '../../services/FournisseurService';
import BonService from '../../services/BonService';

// Styles optimisés pour l'espace
const useStyles = makeStyles((theme) => ({
  paper: {
    padding: '16px 12px 20px 12px',
    borderRadius: 12,
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    border: '1px solid #e0e0e0',
    width: '100%',
    minHeight: 'auto',
  },
  title: {
    fontWeight: 600,
    color: '#226D68',
    fontSize: '1.1rem',
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  compactField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      padding: '10px 12px',
    },
  },
  compactButton: {
    fontSize: '0.8rem',
    padding: '6px 12px',
    fontWeight: 600,
    minWidth: 'auto',
  },
  submitButton: {
    background: 'linear-gradient(135deg, #f26522 0%, #ff8c42 100%)',
    color: 'white',
    fontSize: '0.8rem',
    padding: '8px 16px',
    fontWeight: 600,
    flex: 1,
    '&:hover': {
      background: 'linear-gradient(135deg, #e55a1a 0%, #ff7b2e 100%)',
    },
  },
  resetButton: {
    color: '#f26522',
    borderColor: '#f26522',
    fontSize: '0.8rem',
    padding: '8px 16px',
    fontWeight: 600,
    flex: 1,
  },
  dateSection: {
    backgroundColor: '#f8f9fa',
    padding: '12px',
    borderRadius: 8,
    border: '1px solid #e9ecef',
  },
  gridContainer: {
    margin: '0 -4px',
    '& > .MuiGrid-item': {
      padding: '4px 8px',
    },
  },
}));

const AddCommande = ({ refresh, nums }) => {
  const classes = useStyles();
  const auth = useSelector((state) => state.auth);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [produits, setProduits] = useState([]);
  const [prixUnite, setPrixUnite] = useState(0);
  const [prixPub, setPrixPub] = useState(0);
  const [nomProd, setNomProd] = useState('');
  const [nomFournisseur, setNomFournisseur] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [selectedNumero, setSelectedNumero] = useState(nums[0]?.idNumero || '');

  const formatDate = (date) => {
    if (!date) return '';
    if (typeof date === 'string') return date;
    return date.toISOString().split('T')[0];
  };

  const today = formatDate(new Date());

  const isValidDate = (dateString) => {
    if (!dateString) return true;
    const selectedDate = new Date(dateString);
    const todayDate = new Date(today);
    return selectedDate <= todayDate;
  };

  const validationSchema = Yup.object().shape({
    speudo: Yup.string(),
    idFournisseur: Yup.number().required("Obligatoire"),
    codeIdProd: Yup.string().required("Obligatoire"),
    idNumero: Yup.string(),
    qteComder: Yup.number()
      .required("Obligatoire")
      .min(1, "La quantité doit être supérieure à 0"),
    prixPublic: Yup.number(),
    prixUnitaire: Yup.number(),
    date_expiration: Yup.string().required("Date d'expiration obligatoire"),
    quan_livree: Yup.number()
      .required("Obligatoire")
      .min(0, "La quantité livrée ne peut pas être négative")
  });

  const getInitialValues = () => ({
    speudo: auth?.nom,
    codeIdProd: '',
    qteComder: '',
    quan_livree: '',
    prixPublic: '',
    prixUnitaire: '',
    idFournisseur: '',
    idNumero: selectedNumero,
    date_expiration: ''
  });

  const handleProductChange = useCallback(async (productName) => {
    if (!productName) return;
    
    try {
      const response = await CliniqueService.getNomProd(productName);
      const data = response.data[0];
      if (data) {
        setPrixPub(data.prixProd || 0);
        setPrixUnite(data.prix_unitaire || 0);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
      toast.error('Erreur lors du chargement des informations du produit');
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    try {
      const [fournisseursResponse, produitsResponse] = await Promise.all([
        FournisseurService.getFournisseurs(),
        CliniqueService.getAllProduits()
      ]);

      setFournisseurs(fournisseursResponse.data || []);
      setProduits(produitsResponse.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données initiales');
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const resetFormFields = useCallback((resetForm) => {
    setPrixPub(0);
    setPrixUnite(0);
    setNomProd('');
    setNomFournisseur('');
    setShowDatePicker(false);
    setCustomDate('');
    setDateError('');

    resetForm({
      values: {
        ...getInitialValues(),
        idNumero: selectedNumero
      }
    });
  }, [selectedNumero]);

  const handleCustomDateChange = (event) => {
    const newDate = event.target.value;
    setCustomDate(newDate);
    
    if (newDate && !isValidDate(newDate)) {
      setDateError('La date ne peut pas être supérieure à aujourd\'hui');
    } else {
      setDateError('');
    }
  };

  const handleApplyCustomDate = () => {
    if (customDate && isValidDate(customDate)) {
      setShowDatePicker(false);
      setDateError('');
    } else if (customDate) {
      setDateError('Veuillez choisir une date valide (≤ aujourd\'hui)');
    }
  };

  const handleCancelCustomDate = () => {
    setShowDatePicker(false);
    setCustomDate('');
    setDateError('');
  };

  const handleNumeroChange = (event, setFieldValue) => {
    const newNumero = event.target.value;
    setSelectedNumero(newNumero);
    setFieldValue("idNumero", newNumero);
  };

  const onSubmit = async (values, { resetForm }) => {
    try {
      const dateCommande = customDate || today;
      
      if (!isValidDate(dateCommande)) {
        toast.error('La date de commande ne peut pas être dans le futur');
        return;
      }
      
      const commandeData = {
        codeIdProd: values.codeIdProd,
        qteComder: values.qteComder,
        speudo: auth.nom,
        idFournisseur: values.idFournisseur,
        idNumero: values.idNumero,
        prixPublic: prixPub,
        prixUnitaire: prixUnite,
        dateComde: dateCommande,
        quan_livree: values.quan_livree,
        date_expiration: values.date_expiration,
        nomProd: nomProd,
        nomFournisseur: nomFournisseur
      };

      const response = await BonService.createBon(commandeData);
      
      if (response?.data) {
        toast.success(response.data.message);
        resetFormFields(resetForm);
        refresh();
      } else {
        throw new Error(response?.data?.error || 'Erreur inconnue');
      }
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      toast.error(error.response?.data || error.message);
    }
  };

  const CustomDatePicker = ({ field, form, ...props }) => {
    const handleDateChange = (event) => {
      form.setFieldValue(field.name, event.target.value);
    };

    return (
      <TextField
        {...field}
        {...props}
        type="date"
        InputLabelProps={{
          shrink: true,
        }}
        onChange={handleDateChange}
        fullWidth
        variant="outlined"
        size="small"
        className={classes.compactField}
      />
    );
  };

  return (
    <Box p={0.5}>
      <Paper elevation={2} className={classes.paper}>
        <Box py={1} textAlign="center">
          <Typography variant="h6" className={classes.title}>
            📦 Nouvelle Commande
          </Typography>
        </Box>
        
        <Formik 
          initialValues={getInitialValues()} 
          validationSchema={validationSchema} 
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, setFieldValue, isSubmitting, resetForm }) => (
            <Form noValidate>
              <Grid container className={classes.gridContainer}>
                
                {/* Numéro de commande */}
                <Grid item xs={12}>
                  <Field name="idNumero">
                    {({ field, form }) => (
                      <TextField
                        {...field}
                        select
                        variant="outlined"
                        fullWidth
                        size="small"
                        className={classes.compactField}
                        error={touched.idNumero && !!errors.idNumero}
                        helperText={touched.idNumero && errors.idNumero}
                        SelectProps={{
                          native: true,
                        }}
                        onChange={(e) => handleNumeroChange(e, form.setFieldValue)}
                      >
                        <option value="">Sélectionnez un numéro</option>
                        {nums.map((num) => (
                          <option key={num.idNumero} value={num.idNumero}>
                            {num.numero}
                          </option>
                        ))}
                      </TextField>
                    )}
                  </Field>
                </Grid>

                {/* Fournisseur */}
                <Grid item xs={12}>
                  <Autocomplete
                    options={fournisseurs}
                    getOptionLabel={(option) => option.nomFournisseur}
                    value={fournisseurs.find(f => f.nomFournisseur === nomFournisseur) || null}
                    onChange={(_, value) => {
                      setNomFournisseur(value?.nomFournisseur || '');
                      setFieldValue("idFournisseur", value?.idFournisseur || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Fournisseur"
                        variant="outlined"
                        fullWidth
                        size="small"
                        className={classes.compactField}
                        error={touched.idFournisseur && !!errors.idFournisseur}
                        helperText={touched.idFournisseur && errors.idFournisseur}
                        required
                      />
                    )}
                  />
                </Grid>

                {/* Produit */}
                <Grid item xs={12}>
                  <Autocomplete
                    options={produits}
                    getOptionLabel={(option) => option.nomProd}
                    value={produits.find(p => p.nomProd === nomProd) || null}
                    onChange={(_, value) => {
                      setNomProd(value?.nomProd || '');
                      setFieldValue("codeIdProd", value?.codeIdProd || '');
                      handleProductChange(value?.nomProd);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Produit"
                        variant="outlined"
                        fullWidth
                        size="small"
                        className={classes.compactField}
                        error={touched.codeIdProd && !!errors.codeIdProd}
                        helperText={touched.codeIdProd && errors.codeIdProd}
                        required
                      />
                    )}
                  />
                </Grid>

                {/* Prix en ligne compacte */}
                <Grid item xs={6}>
                  <TextField
                    name="prixUnitaire"
                    label="Prix cession"
                    variant="outlined"
                    fullWidth
                    size="small"
                    className={classes.compactField}
                    value={prixUnite}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    name="prixPublic"
                    label="Prix public"
                    variant="outlined"
                    fullWidth
                    size="small"
                    className={classes.compactField}
                    value={prixPub}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>

                {/* Quantités en ligne compacte */}
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    name="qteComder"
                    label="Qté commandée"
                    type="number"
                    variant="outlined"
                    fullWidth
                    size="small"
                    className={classes.compactField}
                    error={touched.qteComder && !!errors.qteComder}
                    helperText={touched.qteComder && errors.qteComder}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    name="quan_livree"
                    label="Qté livrée"
                    type="number"
                    variant="outlined"
                    fullWidth
                    size="small"
                    className={classes.compactField}
                    error={touched.quan_livree && !!errors.quan_livree}
                    helperText={touched.quan_livree && errors.quan_livree}
                    required
                  />
                </Grid>

                {/* Date d'expiration */}
                <Grid item xs={12}>
                  <Field
                    name="date_expiration"
                    label="Date d'expiration"
                    component={CustomDatePicker}
                    error={touched.date_expiration && !!errors.date_expiration}
                    helperText={touched.date_expiration && errors.date_expiration}
                  />
                </Grid>

                {/* Date de commande compacte */}
                <Grid item xs={12}>
                  <Box className={classes.dateSection}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center">
                        <Today style={{ fontSize: 16, color: '#f26522', marginRight: 6 }} />
                        <Typography variant="body2" color="textSecondary">
                          Date : <strong>{customDate || today}</strong>
                        </Typography>
                      </Box>
                      
                      {!showDatePicker ? (
                        <Button 
                          startIcon={<Edit style={{ fontSize: 14 }} />}
                          className={classes.compactButton}
                          size="small"
                          variant="outlined"
                          onClick={() => setShowDatePicker(true)}
                        >
                          Changer
                        </Button>
                      ) : (
                        <Button 
                          startIcon={<Close style={{ fontSize: 14 }} />}
                          className={classes.compactButton}
                          size="small"
                          variant="outlined"
                          onClick={handleCancelCustomDate}
                        >
                          Annuler
                        </Button>
                      )}
                    </Box>

                    <Collapse in={showDatePicker}>
                      <Box mt={1}>
                        <Box display="flex" gap={0.5} alignItems="center" mb={0.5}>
                          <TextField
                            type="date"
                            value={customDate}
                            onChange={handleCustomDateChange}
                            variant="outlined"
                            size="small"
                            style={{ flex: 1 }}
                            className={classes.compactField}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              max: today
                            }}
                            error={!!dateError}
                          />
                          <Button 
                            variant="contained" 
                            size="small"
                            className={classes.compactButton}
                            style={{ 
                              background: '#f26522', 
                              color: 'white',
                            }}
                            onClick={handleApplyCustomDate}
                            disabled={!customDate || !!dateError}
                          >
                            OK
                          </Button>
                        </Box>
                        
                        {dateError && (
                          <Box 
                            display="flex" 
                            alignItems="center" 
                            gap={0.5}
                            color="#d32f2f"
                            fontSize="0.7rem"
                          >
                            <Warning style={{ fontSize: 14 }} />
                            <Typography variant="caption">
                              {dateError}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </Grid>

                {/* Boutons compacts */}
                <Grid item xs={12}>
                  <Box display="flex" gap={1} mt={1}>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      className={classes.submitButton}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'En cours...' : 'Valider'}
                    </Button>
                    <Button 
                      type="button"
                      variant="outlined" 
                      className={classes.resetButton}
                      onClick={() => resetFormFields(resetForm)}
                      disabled={isSubmitting}
                    >
                      Réinitialiser
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default AddCommande;